/**
 * A campaign's posts, pulled for that campaign and attributed to its roster.
 *
 * The posts on a line were previously borrowed from the creator's invoice,
 * which meant a line showed nothing until an invoice existed — backwards, since
 * the post is live well before anyone invoices for it. The product already
 * serves a campaign's posts at
 * `/campaign/{id}/social_media/feeds/{instagram|youtube}`, so the workspace
 * pulls that for the selected campaign instead.
 *
 * Attribution, verified against the live feed: a YouTube row carries
 * `influencer_id` outright, and an Instagram row carries `user_name`, which is
 * the same string the roster holds as the creator's name. A post that matches
 * neither is left unattributed rather than guessed at.
 *
 * Pure.
 */

export const POST_PLATFORMS = ['instagram', 'youtube'];

const LABELS = {
  reel: 'Reel',
  static: 'Post',
  image: 'Post',
  carousel: 'Carousel',
  story: 'Story',
  video: 'Video',
  short: 'Short',
  live: 'Live',
};

const DEFAULT_LABEL = { instagram: 'Post', youtube: 'Video' };

/** Path segments that are a post prefix, not a post id. */
const PREFIXES = new Set(['p', 'reel', 'reels', 'tv', 'watch', 'shorts', 'post']);

const text = (value) => String(value ?? '').trim();

/** Lowercased, whitespace-collapsed — for comparing creator names. */
function nameKey(value) {
  return text(value).toLowerCase().replace(/\s+/g, ' ');
}

function numberOf(value) {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Whether a feed url actually opens the post.
 *
 * The feed returns `https://www.instagram.com/p/?hl=en` for posts it never
 * resolved. That looks like a link and goes nowhere, which is worse for someone
 * checking delivery than showing no link at all.
 */
export function isUsablePostUrl(value) {
  const raw = text(value);
  if (!raw) return false;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    if (url.searchParams.get('v')) return true;
    const last = url.pathname.split('/').filter(Boolean).pop();
    return Boolean(last) && !PREFIXES.has(last.toLowerCase());
  } catch (e) {
    return false;
  }
}

/**
 * Whether the feed has another page after this one.
 *
 * The two platforms answer differently: the Instagram feed returns a DRF
 * envelope with `next`, the YouTube feed returns `count` and nothing else. So
 * `next` alone stops after the first page and loses 30 of a campaign's 40
 * videos. Counting rows against `count` covers both, with an explicit `next:
 * null` still ending it early.
 *
 * `fetched` is the number of rows read so far, including this page.
 */
export function hasMorePages(body, fetched) {
  if (!body) return false;
  const rows = Array.isArray(body.results) ? body.results.length : 0;
  if (rows === 0) return false;
  if ('next' in body) return Boolean(body.next);
  const total = Number(body.count);
  return Number.isFinite(total) ? fetched < total : false;
}

/** "Reel", "Post", "Video" — the word staff use for the deliverable. */
export function postLabel(row, platform) {
  const type = text(row && row.post_type).toLowerCase();
  return LABELS[type] || DEFAULT_LABEL[platform] || 'Post';
}

/** One feed row reduced to what a roster line shows, or null if unusable. */
export function normalisePost(row, platform) {
  if (!row || row.is_deleted) return null;
  const url = text(row.url);
  if (!isUsablePostUrl(url)) return null;

  return {
    id: `${platform}-${row.id}`,
    platform,
    url,
    label: postLabel(row, platform),
    title: text(row.title),
    thumbnail: text(row.thumbnail) || null,
    userName: text(row.user_name),
    influencerId: row.influencer_id ?? null,
    at: text(row.added_on),
    metrics: {
      reach: numberOf(row.reach),
      views: numberOf(row.view_count) || numberOf(row.video_views),
      likes: numberOf(row.like_count),
      comments: numberOf(row.comment_count),
      engagementRate: numberOf(row.engagement_rate),
    },
  };
}

/**
 * `{[lineId]: [post]}`. A vendor line is never given posts — nobody posts on
 * behalf of an editor or a boosting spend.
 */
export function postsByLine(lines, posts) {
  const roster = (Array.isArray(lines) ? lines : []).filter(
    (line) => line && (line.influencer_id || text(line.influencer_name)),
  );
  const byId = new Map();
  const byName = new Map();
  roster.forEach((line) => {
    if (line.influencer_id) byId.set(String(line.influencer_id), line.id);
    const key = nameKey(line.influencer_name);
    if (key && !byName.has(key)) byName.set(key, line.id);
  });

  return (Array.isArray(posts) ? posts : []).reduce((grouped, post) => {
    if (!post) return grouped;
    const lineId =
      (post.influencerId !== null && post.influencerId !== undefined
        ? byId.get(String(post.influencerId))
        : undefined) ?? byName.get(nameKey(post.userName));
    if (lineId === undefined) return grouped;

    const current = grouped[lineId] || [];
    if (current.some((each) => each.url === post.url)) return grouped;
    return { ...grouped, [lineId]: [...current, post] };
  }, {});
}
